/**
 * NotificationDrawer — 右側通知抽屜（F3）
 * ──────────────────────────────────────────────
 * 用途：取代「Bell icon → 跳整頁 /bulletin」的舊行為（原舊路徑為
 *      /system/notifications，已不存在；統一指向公佈欄整頁列表）。
 * 點擊 Header Bell icon 時由 BaseLayout 控制 open 狀態，本元件只負責：
 *   1. 自身打開時拉取通知列表
 *   2. 提供「未讀 / 全部」切換、單筆標記已讀、全部標記已讀
 *   3. 點擊通知行（非按鈕）跳轉到對應業務模組
 *   4. 底部「查看全部通知」轉跳整頁列表
 *
 * 與 BulletinPage 的差異：
 *   - BulletinPage 是發佈與管理公告（含新增/刪除）
 *   - 本 Drawer 只關注「我」收到的通知（讀／已讀切換／跳轉）
 *
 * 視覺對齊 archetype shell：tokens / StatusPill / EmptyState。
 *
 * API 約定（沿用 /api/notifications）：
 *   - GET    /api/notifications?unread=1                → { items, unreadCount }
 *   - PATCH  /api/notifications/:id/read                → 標記單筆已讀
 *   - PATCH  /api/notifications/read-all                → 全部標記已讀
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Segmented, Spin, Tooltip } from 'antd';
import { message } from '@/lib/antd-app';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  Bell,
  CheckCheck,
  CheckCircle2,
  Clock,
  Info,
  XCircle,
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-tw';
import { apiClient, ApiError } from '@/lib/api-client';
import { useRouter } from '@/lib/navigation';
import { EmptyState, StatusPill } from '@/components/shell';
import {
  color,
  ease,
  font,
  leading,
  motion,
  radius,
  space,
  text,
  tracking,
  weight,
} from '@/styles/tokens';

/* dayjs 相對時間（中文台灣） */
dayjs.extend(relativeTime);
dayjs.locale('zh-tw');

/* ════════════════════════════════════════════════
 * 類型 / 常量
 * ════════════════════════════════════════════════ */

type NotificationType = 'info' | 'warning' | 'success' | 'error';
type FilterMode = 'unread' | 'all';

/** 後端回傳的單筆通知形狀（與 BulletinPage 一致） */
interface NotificationItem {
  id: number;
  userId: number;
  title: string;
  content: string | null;
  type: NotificationType;
  isRead: boolean;
  /** 業務模組 key，例：'audit-tasks' / 'applications' */
  relatedModule: string | null;
  /** 業務記錄 ID（字串以兼容多種主鍵） */
  relatedId: string | null;
  createdAt: string;
}

interface PaginatedResult {
  items: NotificationItem[];
  total: number;
  page: number;
  pageSize: number;
}

/** Drawer 內列表單次最多顯示的條數（避免長到沒底） */
const PAGE_SIZE = 30;

/** 類型對應的視覺配置：StatusPill tone + icon + 強調色 */
const TYPE_CONFIG: Record<
  NotificationType,
  {
    label: string;
    tone: 'info' | 'warning' | 'success' | 'danger';
    icon: React.ReactNode;
    accent: string;
  }
> = {
  info: {
    label: '一般通知',
    tone: 'info',
    icon: <Info size={14} />,
    accent: color.state.info,
  },
  warning: {
    label: '注意事項',
    tone: 'warning',
    icon: <AlertTriangle size={14} />,
    accent: color.state.warning,
  },
  success: {
    label: '好消息',
    tone: 'success',
    icon: <CheckCircle2 size={14} />,
    accent: color.state.success,
  },
  error: {
    label: '重要通知',
    tone: 'danger',
    icon: <XCircle size={14} />,
    accent: color.state.danger,
  },
};

/**
 * 業務模組 → 路由前綴 對照
 * 用於決定點擊一行時跳到哪個頁面。未列入者 fallback 到 /bulletin。
 */
const MODULE_ROUTE_MAP: Record<string, string> = {
  'audit-tasks': '/audit-tasks',
  applications: '/applications',
  certificates: '/certificates',
  clients: '/clients',
  'nc-reports': '/nc-reports',
  leads: '/leads',
  contracts: '/contracts',
  payments: '/payments',
};

/* ════════════════════════════════════════════════
 * 子組件：單則通知 row
 * ════════════════════════════════════════════════ */

interface NotificationRowProps {
  item: NotificationItem;
  onMarkRead: (id: number) => void;
  onActivate: (item: NotificationItem) => void;
}

/**
 * 一行通知卡：左側類型 chip / 中間標題與摘要 / 右側標記已讀按鈕
 *
 * 互動規範：
 *   - 整行可點：觸發 onActivate（自動標記已讀 + 跳轉）
 *   - 「標記已讀」按鈕：阻止冒泡，僅標記不跳轉
 */
function NotificationRow({ item, onMarkRead, onActivate }: NotificationRowProps) {
  const config = TYPE_CONFIG[item.type] ?? TYPE_CONFIG.info;
  const isUnread = !item.isRead;

  /** 相對時間（>7 天用絕對日期，避免「3 個月前」誤導） */
  const formatTime = (dateStr: string) => {
    const date = dayjs(dateStr);
    if (dayjs().diff(date, 'day') > 7) return date.format('YYYY-MM-DD HH:mm');
    return date.fromNow();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onActivate(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onActivate(item);
        }
      }}
      style={{
        display: 'flex',
        gap: space[3],
        padding: `${space[4]}px ${space[5]}px`,
        borderBottom: `1px solid ${color.border.subtle}`,
        background: isUnread ? color.surface.muted : 'transparent',
        position: 'relative',
        cursor: 'pointer',
        transition: `background ${motion.fast} ${ease.out}`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = color.surface.sunken;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = isUnread ? color.surface.muted : 'transparent';
      }}
    >
      {/* 左邊未讀色條 */}
      {isUnread && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            background: config.accent,
          }}
          aria-hidden
        />
      )}

      {/* 中間內容區 */}
      <div style={{ minWidth: 0, flex: 1 }}>
        {/* meta 行：類型 chip + 時間 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: space[2],
            marginBottom: space[2],
            flexWrap: 'wrap',
          }}
        >
          <StatusPill tone={config.tone} icon={config.icon} size="sm">
            {config.label}
          </StatusPill>
          {isUnread && (
            <StatusPill tone="brand" dot size="sm" variant="outline">
              未讀
            </StatusPill>
          )}
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: text.xs,
              color: color.text.muted,
              marginLeft: 'auto',
            }}
          >
            <Clock size={12} />
            {formatTime(item.createdAt)}
          </span>
        </div>

        {/* 標題（Manrope display 字體） */}
        <div
          style={{
            fontFamily: font.display,
            fontSize: text.md,
            fontWeight: isUnread ? weight.bold : weight.semibold,
            color: color.text.primary,
            letterSpacing: tracking.tight,
            lineHeight: leading.tight,
            marginBottom: item.content ? 4 : 0,
            /* 單行截斷：標題不允許換行 */
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.title}
        </div>

        {/* 摘要（Inter body，1 行截斷） */}
        {item.content && (
          <div
            style={{
              fontFamily: font.body,
              fontSize: text.sm,
              color: color.text.tertiary,
              lineHeight: leading.snug,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.content}
          </div>
        )}
      </div>

      {/* 右側操作：標記已讀（僅未讀顯示） */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          flexShrink: 0,
          paddingTop: 2,
        }}
      >
        {isUnread ? (
          <Tooltip title="標記已讀">
            <Button
              type="text"
              size="small"
              icon={<CheckCircle2 size={14} />}
              /* 阻止冒泡，避免觸發整行 onActivate */
              onClick={(e) => {
                e.stopPropagation();
                onMarkRead(item.id);
              }}
              style={{ color: color.state.success }}
              aria-label="標記已讀"
            />
          </Tooltip>
        ) : (
          /* 已讀：顯示一個淡色箭頭，視覺對齊 */
          <span
            style={{
              display: 'inline-flex',
              width: 24,
              height: 24,
              alignItems: 'center',
              justifyContent: 'center',
              color: color.text.muted,
            }}
            aria-hidden
          >
            <ArrowRight size={14} />
          </span>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
 * 主元件：NotificationDrawer
 * ════════════════════════════════════════════════ */

export interface NotificationDrawerProps {
  /** 抽屜是否開啟（由 BaseLayout 控制） */
  open: boolean;
  /** 關閉抽屜的回呼 */
  onClose: () => void;
}

export default function NotificationDrawer({ open, onClose }: NotificationDrawerProps) {
  const router = useRouter();
  /* TanStack Query client：用於在 mark-read 後失效 NotificationBell 的未讀數查詢，
   * 讓 Header 鈴鐺徽章立即刷新而不必等 30s 輪詢。 */
  const queryClient = useQueryClient();

  /* ── 狀態 ── */
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterMode>('unread');
  /** 全部已讀按鈕的 loading 旗標 */
  const [markingAll, setMarkingAll] = useState(false);

  /* ── 取資料：拉一頁列表（依 filter 決定是否帶 isRead=false） ── */
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        pageSize: String(PAGE_SIZE),
      });
      /* 後端 GET /api/notifications?unread=1 — 過濾僅未讀 */
      if (filter === 'unread') params.set('unread', '1');

      const result = await apiClient.get<PaginatedResult>(
        `/api/notifications?${params.toString()}`,
      );
      setNotifications(result.items ?? []);
    } catch (err) {
      console.error('[NotificationDrawer] fetch failed', err);
      message.error('取得通知列表失敗');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  /* 抽屜開啟、或 filter 切換時，重抓一次 */
  useEffect(() => {
    if (!open) return;
    void fetchNotifications();
  }, [open, fetchNotifications]);

  /* ── 客端過濾：兜底 / 立即反映本地已讀變更 ── */
  const visibleList = useMemo(() => {
    if (filter === 'unread') return notifications.filter((n) => !n.isRead);
    return notifications;
  }, [notifications, filter]);

  /* ── 操作：單筆已讀 ── */
  const handleMarkRead = useCallback(
    async (id: number) => {
      /* 樂觀更新：立即把該筆改成 isRead=true */
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
      try {
        await apiClient.patch(`/api/notifications/${id}/read`);
        /* 失效鈴鐺未讀數查詢，讓 Header Badge 立即重新計算 */
        void queryClient.invalidateQueries({ queryKey: ['notifications', 'unreadCount'] });
      } catch (err) {
        console.error('[NotificationDrawer] mark-read failed', err);
        message.error('標記已讀失敗');
        /* 失敗回滾 */
        setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: false } : n)));
      }
    },
    [queryClient],
  );

  /* ── 操作：全部已讀（優先 mark-all-read endpoint，失敗時逐筆 PUT） ── */
  const handleMarkAllRead = useCallback(async () => {
    const unreadIds = notifications.filter((n) => !n.isRead).map((n) => n.id);
    if (unreadIds.length === 0) {
      message.info('目前沒有未讀通知');
      return;
    }

    setMarkingAll(true);
    /* 樂觀更新整個列表 */
    const snapshot = notifications;
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));

    try {
      await apiClient.patch('/api/notifications/read-all');
      message.success(`已將 ${unreadIds.length} 則通知標記為已讀`);
      /* 失效鈴鐺未讀數查詢，Header Badge 立即歸零 */
      void queryClient.invalidateQueries({ queryKey: ['notifications', 'unreadCount'] });
    } catch (err) {
      /* endpoint 不存在 / 失敗：退回逐筆 PATCH */
      const isNotFound = err instanceof ApiError && err.status === 404;
      if (!isNotFound) {
        console.error('[NotificationDrawer] read-all api failed, fallback to per-id PATCH', err);
      }
      try {
        await Promise.all(unreadIds.map((id) => apiClient.patch(`/api/notifications/${id}/read`)));
        message.success(`已將 ${unreadIds.length} 則通知標記為已讀`);
        void queryClient.invalidateQueries({ queryKey: ['notifications', 'unreadCount'] });
      } catch (fallbackErr) {
        console.error('[NotificationDrawer] read-all fallback failed', fallbackErr);
        message.error('全部標記已讀失敗，請稍後重試');
        /* 失敗回滾為原本快照 */
        setNotifications(snapshot);
      }
    } finally {
      setMarkingAll(false);
    }
  }, [notifications, queryClient]);

  /* ── 操作：點整行 → 標記已讀 + 跳轉 ── */
  const handleActivate = useCallback(
    (item: NotificationItem) => {
      /* 先觸發已讀（不阻塞跳轉，async 可丟失敗也沒關係） */
      if (!item.isRead) {
        void handleMarkRead(item.id);
      }

      /* 沒有業務關聯 → fallback 到通知整頁 */
      if (!item.relatedModule || !item.relatedId) {
        onClose();
        router.push('/bulletin');
        return;
      }

      /* 解析業務路徑（未匹配時 fallback 到通知整頁） */
      const prefix = MODULE_ROUTE_MAP[item.relatedModule];
      if (!prefix) {
        onClose();
        router.push('/bulletin');
        return;
      }
      onClose();
      router.push(`${prefix}/${item.relatedId}`);
    },
    [handleMarkRead, onClose, router],
  );

  /* ── 操作：查看全部通知 ── */
  const handleGoAll = useCallback(() => {
    onClose();
    router.push('/bulletin');
  }, [onClose, router]);

  /* ── 統計：未讀數量（用於空狀態文案） ── */
  const unreadCount = useMemo(() => notifications.filter((n) => !n.isRead).length, [notifications]);

  /* ════════════════════════════════════════════════
   * Render
   * ════════════════════════════════════════════════ */

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      size={420}
      /* 自訂 Header：標題 + 全部已讀。closable 改 false，自畫 close 由 antd Drawer 預設 X 處理 */
      title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: space[2],
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: radius.md,
              background: color.brand.primarySoft,
              color: color.brand.primary,
            }}
          >
            <Bell size={16} />
          </span>
          <span
            style={{
              fontFamily: font.display,
              fontSize: text.lg,
              fontWeight: weight.bold,
              color: color.text.primary,
              letterSpacing: tracking.tight,
            }}
          >
            通知中心
          </span>
        </div>
      }
      extra={
        /* Header 右上：全部已讀（text 樣式保持輕量） */
        <Button
          type="text"
          size="small"
          icon={<CheckCheck size={14} />}
          onClick={handleMarkAllRead}
          loading={markingAll}
          disabled={unreadCount === 0}
          style={{ color: color.brand.primary }}
        >
          全部已讀
        </Button>
      }
      /* Body 不留內距：列表自帶 padding；底部 footer 自訂 */
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        header: {
          borderBottom: `1px solid ${color.border.subtle}`,
          padding: `${space[3]}px ${space[5]}px`,
        },
      }}
      footer={
        /* Sticky 底部：查看全部通知 */
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: `${space[2]}px ${space[2]}px`,
          }}
        >
          <span
            style={{
              fontSize: text.xs,
              color: color.text.muted,
            }}
          >
            {unreadCount > 0 ? `${unreadCount} 則未讀` : '目前無未讀'}
          </span>
          <Button type="link" size="small" onClick={handleGoAll}>
            查看全部通知 <ArrowRight size={12} />
          </Button>
        </div>
      }
    >
      {/* 篩選列：未讀 / 全部 */}
      <div
        style={{
          padding: `${space[3]}px ${space[5]}px`,
          borderBottom: `1px solid ${color.border.subtle}`,
          background: color.surface.base,
        }}
      >
        <Segmented
          block
          value={filter}
          onChange={(v) => setFilter(v as FilterMode)}
          options={[
            { label: '未讀', value: 'unread' },
            { label: '全部', value: 'all' },
          ]}
        />
      </div>

      {/* 列表主體：自身可滾動，flex:1 撐滿 footer 上方空間 */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 0,
        }}
      >
        <Spin spinning={loading}>
          {visibleList.length === 0 && !loading ? (
            <EmptyState
              variant="empty"
              icon={<Bell size={32} strokeWidth={1.5} />}
              title={filter === 'unread' ? '目前無未讀通知' : '尚無任何通知'}
              description={
                filter === 'unread'
                  ? '所有通知都已處理，您可切換到「全部」查看歷史紀錄'
                  : '系統將在重要事件發生時於此提醒您'
              }
              size="md"
            />
          ) : (
            visibleList.map((item) => (
              <NotificationRow
                key={item.id}
                item={item}
                onMarkRead={handleMarkRead}
                onActivate={handleActivate}
              />
            ))
          )}
        </Spin>
      </div>
    </Drawer>
  );
}
